// Guided demo scripts (run on seeded demo tenants). One storyboard per demo
// tenant, resolved via the TOURS registry at the bottom of this file.
//
// FERCO storyboard (TOUR_STEPS) follows docs/demo-ferco/RUNBOOK.md:
// gemelo digital -> Torre de Control -> Analyst -> capital congelado ->
// agentes + aprobaciones -> process mining -> planificador -> LAB -> WhatsApp.
//
// The overlay resolves each step in order: navigate to `page`, optionally
// click `clickSelector` (skipped when `target` is already in the DOM, so a
// user who clicked the highlighted element themselves doesn't re-trigger it),
// then anchor the card to `target`. Steps without `target` render centered.

export interface TourStep {
  id: string;
  /** navigationStore page id to land on before showing the card. */
  page?: string;
  /** Best-effort click after navigation (tab, tile, back button). */
  clickSelector?: string;
  /** CSS selector the card anchors + spotlights. Centered card when absent. */
  target?: string;
  /** Visible label of the control to spotlight ("Nueva fuente"), for steps
   *  written without knowing our selectors (Assistant guides). Used only when
   *  `target` is absent; centered card when nothing on screen matches. */
  targetText?: string;
  title: string;
  body: string;
  /** Celonis-style bracketed instruction line, rendered in brand purple. */
  action?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** Clicking the spotlighted element advances to the next step. */
  advanceOnTargetClick?: boolean;
  /** Pre-type text into an input/textarea after the step resolves, so the
   *  presenter only has to hit Enter (e.g. the agent chat prompt). */
  fill?: { selector: string; text: string };
  /** PAI Academy: step is proof-gated — the overlay shows a Verificar button
   *  and blocks Next until the academy service confirms the build. */
  proof?: boolean;
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    title: 'Nexus — Recorrido guiado',
    body: 'Bienvenido. En los proximos minutos vas a ver como Nexus convierte los datos que tu empresa ya tiene — catalogo, inventario, proyectos e importaciones — en un gemelo digital operativo del negocio.\n\nAvanza con las flechas de esta tarjeta o con las teclas de tu teclado. Esc sale del recorrido.',
  },
  {
    id: 'ontology',
    page: 'ontology',
    target: '[data-tour="ontology-canvas"]',
    title: 'El Gemelo Digital',
    body: 'Todo empieza aqui: Coleccion, SKU, Sublote (tono y calibre), Existencia, Reserva y Proyecto, mas Contenedor y Proveedor, conectados como un grafo vivo.\n\nEl inventario real no es el SKU: es el sublote. Sobre este modelo trabaja todo lo que sigue.',
  },
  {
    id: 'connector-card',
    page: 'connectors',
    target: '[data-tour-connector*="VTEX"]',
    title: 'Como se alimenta el gemelo',
    body: 'Los datos entran solos. Este conector se conecta al VTEX de la empresa y alimenta dos objetos del gemelo a la vez: el catalogo (SKU) y el inventario (Existencia).\n\nUna sola conexion, dos objetos vivos — sin exportar Excels ni copiar nada a mano.',
    action: '[Haz clic en la tarjeta]',
    advanceOnTargetClick: true,
  },
  {
    id: 'connector-pipelines',
    clickSelector: '[data-tour="connector-tab-pipelines"]',
    target: '[data-tour="connector-pipelines"]',
    placement: 'left',
    title: 'Un conector, dos objetos',
    body: 'Dos pipelines salen del mismo conector: uno mantiene el catalogo (SKU), el otro las existencias por tienda y CEDI.\n\nCada corrida actualiza el gemelo — y todo lo que viste y veras (dashboards, agentes, predicciones) se recalcula sobre esos datos frescos.',
  },
  {
    id: 'torre-tile',
    page: 'apps',
    clickSelector: '[data-tour="app-back"]',
    target: '[data-tour-app*="Torre de Control"]',
    title: 'Dashboards ejecutivos en vivo',
    body: 'El gemelo alimenta dashboards que se recalculan en tiempo real. Empecemos por la Torre de Control del CEO.',
    action: '[Haz clic en la tarjeta]',
    advanceOnTargetClick: true,
  },
  {
    id: 'torre-open',
    clickSelector: '[data-tour-app*="Torre de Control"]',
    target: '[data-tour="app-topbar"]',
    placement: 'bottom',
    title: 'Torre de Control',
    body: 'Q23.4M de capital congelado, 7 proyectos abiertos en riesgo y Q18M de valor expuesto — con la razon exacta de cada riesgo en la tabla.\n\nEsto no es un reporte de ayer: se calcula sobre el gemelo cada vez que lo abres.',
  },
  {
    id: 'analyst',
    page: 'workbench',
    target: '[data-tour="workbench-analyst"]',
    placement: 'left',
    title: 'Pregunta en lenguaje natural',
    body: 'El Analista responde preguntas de negocio directamente sobre el gemelo. Por ejemplo:\n\n"Muestrame todos los proyectos abiertos en riesgo porque no hay sublote homogeneo suficiente, ordenados por el valor en riesgo."\n\nDevuelve los 7 proyectos con m2 faltantes y la razon exacta de cada uno.',
  },
  {
    id: 'capital-tile',
    page: 'apps',
    clickSelector: '[data-tour="app-back"]',
    target: '[data-tour-app*="Capital Congelado"]',
    title: 'La vista del CFO',
    body: 'Q23.4M congelados hoy; Q8.7M recuperables en 60 dias sin comprar nada. Veamos de donde sale ese numero.',
    action: '[Haz clic en la tarjeta]',
    advanceOnTargetClick: true,
  },
  {
    id: 'capital-open',
    clickSelector: '[data-tour-app*="Capital Congelado"]',
    target: '[data-tour="app-topbar"]',
    placement: 'bottom',
    title: 'Capital Congelado y Rebalanceo',
    body: 'Tres secciones: capital congelado por coleccion y ubicacion con su aging; transferencias sugeridas por el Agente de Reposicion con la justificacion de cada una; y cobertura critica — SKUs calientes con menos de 2 semanas de stock en una tienda mientras el mismo sublote duerme en otra.',
  },
  {
    id: 'agent-open',
    page: 'agents',
    target: '[data-tour-agent*="Reposici"]',
    title: 'Agentes que trabajan por ti',
    body: 'En Agent Studio viven los agentes del negocio: Reposicion, Planificador de Importacion y el asistente de WhatsApp.\n\nVamos a poner a trabajar al Agente de Reposicion — en vivo.',
    action: '[Haz clic en el Agente de Reposicion]',
    advanceOnTargetClick: true,
  },
  {
    id: 'agent-chat',
    clickSelector: '[data-tour="agent-tab-chat"]',
    target: '[data-tour="agent-chat-input"]',
    placement: 'top',
    fill: {
      selector: '[data-tour="agent-chat-input"]',
      text: 'Proponme las 3 transferencias entre tiendas mas valiosas para liberar capital congelado, con su justificacion.',
    },
    title: 'Pidele trabajo, no datos',
    body: 'Ya te dejamos la instruccion escrita. El agente va a cruzar existencias por sublote con la rotacion de cada tienda, elegir las 3 transferencias mas valiosas y proponerlas formalmente.',
    action: '[Envia el mensaje con Enter]',
  },
  {
    id: 'agent-run',
    target: '[data-tour="agent-chat-stream"]',
    placement: 'left',
    title: 'Mira como razona',
    body: 'Cada paso del agente es visible: consulta el gemelo, calcula, y al final llama action_propose — una propuesta formal por cada transferencia.\n\nImportante: el agente NO ejecuto nada. Cada propuesta quedo en espera de aprobacion humana. Cuando termine, pasa al siguiente paso.',
  },
  {
    id: 'actions-inbox',
    page: 'human-actions',
    target: '[data-tour="actions-queue"]',
    placement: 'right',
    title: 'Las propuestas acaban de llegar',
    body: 'Este es el buzon de aprobaciones. Las transferencias que el agente propuso hace unos segundos ya estan aqui, marcadas con la etiqueta Agent y con el razonamiento completo de cada una.',
    action: '[Haz clic en una transferencia para revisarla]',
    advanceOnTargetClick: true,
  },
  {
    id: 'action-approve',
    target: '[data-tour="action-approve"]',
    placement: 'top',
    title: 'Tu apruebas, Nexus ejecuta',
    body: 'Los agentes nunca escriben directo a los sistemas. Al aprobar, Nexus crea la Transferencia en el gemelo y queda auditado quien aprobo, cuando y por que.\n\nEse es el modelo: los agentes proponen y preparan; las personas deciden.',
    action: '[Haz clic en Approve]',
    advanceOnTargetClick: true,
  },
  {
    id: 'pm-tab',
    page: 'activity',
    target: '[data-tour="activity-tab-process"]',
    placement: 'bottom',
    title: 'Los procesos, como son de verdad',
    body: 'Nexus reconstruye el flujo real del negocio a partir de event logs que ya existen — nadie los estaba leyendo.',
    action: '[Haz clic en Process Mining]',
    advanceOnTargetClick: true,
  },
  {
    id: 'pm-overview',
    clickSelector: '[data-tour="activity-tab-process"]',
    target: '[data-tour="process-mining"]',
    title: 'Cotizacion a Entrega (Proyectos)',
    body: '260 casos reconstruidos. El camino feliz cubre apenas ~35%; el resto son desvios que cuestan dias y ventas.\n\nEl ciclo importa: las cotizaciones que cierran en menos de 15 dias ganan ~75% de las veces; las que pasan de 30 dias, solo ~22%.',
  },
  {
    id: 'pm-map',
    target: '[data-tour="pm-tab-map"]',
    placement: 'bottom',
    title: 'El mapa del proceso',
    body: 'En Process Map, el slider de variantes revela lo que el proceso ideal esconde: el loop de recotizacion (AJUSTE_SOLICITADO a RECOTIZADO, ~22% de las cotizaciones) y la rama SIN_SUBLOTE_SUFICIENTE a PO_IMPORTACION, que agrega 45 a 70 dias por importacion.',
    action: '[Explora la pestana Process Map]',
  },
  {
    id: 'pm-bottlenecks',
    target: '[data-tour="pm-tab-bottlenecks"]',
    placement: 'bottom',
    title: 'Donde mueren las cotizaciones',
    body: 'EN_APROBACION_CLIENTE es el cuello de botella: ~30% de las cotizaciones se estancan ahi (mediana ~18 dias) y 87 pasaron directo a COTIZACION_PERDIDA.\n\nY de FACTURADA a COBRADA: ~37 dias de cobro.',
    action: '[Explora la pestana Bottlenecks]',
  },
  {
    id: 'importacion-tile',
    page: 'apps',
    clickSelector: '[data-tour="app-back"]',
    target: '[data-tour-app*="Planificador de Importaci"]',
    title: 'Compras + CFO',
    body: 'El plan de importacion completo, calculado desde el gemelo: que ordenar, cuantos contenedores y cuando colocar cada PO.',
    action: '[Haz clic en la tarjeta]',
    advanceOnTargetClick: true,
  },
  {
    id: 'importacion-open',
    clickSelector: '[data-tour-app*="Planificador de Importaci"]',
    target: '[data-tour="app-topbar"]',
    placement: 'bottom',
    title: 'Planificador de Importacion',
    body: 'Cambia el escenario de demanda de base a tendencia alta y el plan se recalcula por coleccion: de 49.6k m2 a 165.5k m2, con contenedores y fecha de colocacion segun el lead time real de cada proveedor.\n\nAbajo, los contenedores en el agua: 12 en pipeline, 7 en transito, con ETAs reales a Quetzal y Santo Tomas.',
  },
  {
    id: 'insights',
    page: 'insights',
    target: '[data-tour="nav-insights"]',
    placement: 'right',
    title: 'Lo que Nexus descubre solo',
    body: 'El motor de correlaciones encontro, sin que nadie se lo pidiera, que SIN_SUBLOTE_SUFICIENTE implica PO_IMPORTACION (lift 6.5) y las diferencias reales de lead time entre proveedores.',
  },
  {
    id: 'predictions',
    page: 'predictions',
    target: '[data-tour="nav-predictions"]',
    placement: 'right',
    title: 'Predicciones sobre casos abiertos',
    body: '42 modelos entrenados sobre el historico. Cada cotizacion abierta se puntua en vivo — por ejemplo, los dias estimados hasta la decision del cliente, con la explicacion de que factores pesan.',
  },
  {
    id: 'whatsapp',
    title: 'Nexus para distribuidores',
    body: 'El mismo gemelo atiende WhatsApp. Un distribuidor escribe: "Necesito 180 m2 de porcelanato Rocko, hay algun sublote homogeneo con suficiente?"\n\nEl asistente responde con sublotes reales (tono y calibre), en que tienda o CEDI estan, advierte sobre mezclar tonos y arma la cotizacion. El pedido cae de vuelta en la ontologia.',
  },
  {
    id: 'close',
    title: 'Fin del recorrido',
    body: 'Un solo gemelo digital: dashboards ejecutivos, process mining, agentes con aprobacion humana, predicciones y un canal de WhatsApp — todo sobre datos que la empresa ya tiene.\n\nEste entorno esta vivo: haz clic donde quieras y explora.',
  },
];

// IPSA storyboard follows docs/demo-ipsa/RUNBOOK.md (office furniture over
// SAP B1, Honduras): gemelo de inventarios y compras -> conector SAP B1 ->
// Torre de Control -> Analyst -> capital inmovilizado -> agente de
// redistribucion + aprobaciones -> process mining (pedido a entrega, compra a
// bodega) -> planificador de compras 40HQ -> LAB -> copiloto de bodega.
export const IPSA_STEPS: TourStep[] = [
  {
    id: 'welcome',
    title: 'Nexus — Recorrido guiado',
    body: 'Bienvenido. En los proximos minutos vas a ver como Nexus convierte los datos que ya viven en SAP Business One — articulos, stock por bodega, apartados, ordenes de compra y contenedores — en un gemelo digital operativo de inventarios y compras.\n\nSin reemplazar SAP: Nexus vive encima, como capa de contexto. Avanza con las flechas de esta tarjeta o con las teclas de tu teclado. Esc sale del recorrido.',
  },
  {
    id: 'ontology',
    page: 'ontology',
    target: '[data-tour="ontology-canvas"]',
    title: 'El Gemelo Digital',
    body: 'Todo empieza aqui: Linea, SKU importado (prefijo I-), Existencia por bodega — disponible frente a apartado —, Apartado (lock-in), Pedido de Cliente, mas Orden de Compra, Contenedor 40HQ y Proveedor, conectados como un grafo vivo.\n\nEl inventario real no es el total de SAP: es cuanto hay disponible en CADA bodega, y cuanto esta apartado y desde cuando. Sobre este modelo trabaja todo lo que sigue.',
  },
  {
    id: 'connector-card',
    page: 'connectors',
    target: '[data-tour-connector*="SAP Business One"]',
    title: 'Como se alimenta el gemelo',
    body: 'Los datos entran solos. Este conector se conecta al Service Layer de SAP Business One, en modo solo lectura, y alimenta dos objetos del gemelo a la vez: el maestro de articulos (SKU) y el stock por almacen (Existencia).\n\nUna sola conexion, dos objetos vivos — sin exportar Excels ni cruzar reportes de ayer a mano.',
    action: '[Haz clic en la tarjeta]',
    advanceOnTargetClick: true,
  },
  {
    id: 'connector-pipelines',
    clickSelector: '[data-tour="connector-tab-pipelines"]',
    target: '[data-tour="connector-pipelines"]',
    placement: 'left',
    title: 'Un conector, dos objetos',
    body: 'Dos pipelines salen del mismo conector: uno mantiene el maestro de articulos I- (OITM), el otro las existencias por bodega y sucursal (OITW), con disponible y apartado.\n\nCada corrida actualiza el gemelo — y todo lo que veras (dashboards, agentes, predicciones) se recalcula sobre esos datos frescos. Las transacciones siguen viviendo en SAP.',
  },
  {
    id: 'torre-tile',
    page: 'apps',
    clickSelector: '[data-tour="app-back"]',
    target: '[data-tour-app*="Torre de Control"]',
    title: 'Dashboards ejecutivos en vivo',
    body: 'El gemelo alimenta dashboards que se recalculan en tiempo real. Empecemos por la Torre de Control de Gerencia y Operaciones.',
    action: '[Haz clic en la tarjeta]',
    advanceOnTargetClick: true,
  },
  {
    id: 'torre-open',
    clickSelector: '[data-tour-app*="Torre de Control"]',
    target: '[data-tour="app-topbar"]',
    placement: 'bottom',
    title: 'Torre de Control',
    body: '$2.6M de inventario importado a costo rotando en ~210 dias; $870K por encima de la cobertura de 180 dias, SKU por SKU y bodega por bodega; $81K en producto a descontinuar; y los apartados que ya vencieron.\n\nEsto no es un reporte de ayer: se calcula sobre el gemelo cada vez que lo abres. Abajo, la tabla que SAP y Excel no cruzan solos — y, al final, las cuatro funciones que IPSA pidio por nombre y donde vive cada una: posicion en bodega por rotacion, alarmas de rotacion, cubicaje del camion y orden de compra sugerida.',
  },
  {
    id: 'analyst',
    page: 'workbench',
    target: '[data-tour="workbench-analyst"]',
    placement: 'left',
    title: 'Pregunta en lenguaje natural',
    body: 'El Analista responde preguntas de negocio directamente sobre el gemelo. Por ejemplo:\n\n"Muestrame los SKUs con menos de 21 dias de cobertura en una bodega mientras el mismo SKU tiene mas de 180 dias en otra, ordenados por el capital inmovilizado."\n\nDevuelve la lista con las dos bodegas, las unidades y los dias de cobertura de cada una. Cada usuario pregunta lo suyo: la Jefa de Compras, el Gerente Comercial, Operaciones, Bodega.',
  },
  {
    id: 'capital-tile',
    page: 'apps',
    clickSelector: '[data-tour="app-back"]',
    target: '[data-tour-app*="Capital Inmovilizado"]',
    title: 'La vista de Finanzas y Compras',
    body: '$870K por encima de la cobertura meta; cerca de $190K de compra evitable moviendo stock entre bodegas sin comprar nada. Veamos de donde sale ese numero.',
    action: '[Haz clic en la tarjeta]',
    advanceOnTargetClick: true,
  },
  {
    id: 'capital-open',
    clickSelector: '[data-tour-app*="Capital Inmovilizado"]',
    target: '[data-tour="app-topbar"]',
    placement: 'bottom',
    title: 'Capital Inmovilizado y Redistribucion',
    body: 'Cuatro secciones: exceso y dias de cobertura por linea y por bodega — el mapa de capital inmovilizado valorizado —; redistribuciones sugeridas por el Agente de Redistribucion con justificacion, metros cubicos y viajes de camion; cobertura critica — SKUs con menos de 3 semanas de stock en una sucursal mientras la bodega central acumula meses —; y el stock muerto con su aging.\n\nEl agente corre solo cada lunes a las 6:00; hoy lo vamos a correr en vivo.',
  },
  {
    id: 'agent-open',
    page: 'agents',
    target: '[data-tour-agent*="Redistribuci"]',
    title: 'Agentes que trabajan por ti',
    body: 'En Agent Studio viven los agentes del negocio: Redistribucion, Planificador de Compras y el asistente de bodega por WhatsApp.\n\nVamos a poner a trabajar al Agente de Redistribucion — en vivo.',
    action: '[Haz clic en el Agente de Redistribucion]',
    advanceOnTargetClick: true,
  },
  {
    id: 'agent-chat',
    clickSelector: '[data-tour="agent-tab-chat"]',
    target: '[data-tour="agent-chat-input"]',
    placement: 'top',
    fill: {
      selector: '[data-tour="agent-chat-input"]',
      text: 'Proponme las 3 redistribuciones entre bodegas mas valiosas para evitar stockouts y liberar capital inmovilizado, con la justificacion y la instruccion para el personal de bodega.',
    },
    title: 'Pidele trabajo, no datos',
    body: 'Ya te dejamos la instruccion escrita. El agente va a cruzar las existencias por bodega con la venta semanal de cada una, elegir las 3 redistribuciones mas valiosas y proponerlas formalmente — con la instruccion clara para bodega: que retirar, de donde, cuantos metros cubicos y donde ubicarlo.',
    action: '[Envia el mensaje con Enter]',
  },
  {
    id: 'agent-run',
    target: '[data-tour="agent-chat-stream"]',
    placement: 'left',
    title: 'Mira como razona',
    body: 'Cada paso del agente es visible: consulta el gemelo, calcula, y al final llama action_propose — una propuesta formal por cada redistribucion.\n\nImportante: el agente NO ejecuto nada. Cada propuesta quedo en espera de aprobacion humana. Cuando termine, pasa al siguiente paso.',
  },
  {
    id: 'actions-inbox',
    page: 'human-actions',
    target: '[data-tour="actions-queue"]',
    placement: 'right',
    title: 'Las propuestas acaban de llegar',
    body: 'Este es el buzon de aprobaciones. Las redistribuciones que el agente propuso hace unos segundos ya estan aqui, marcadas con la etiqueta Agent y con el razonamiento completo de cada una.',
    action: '[Haz clic en una redistribucion para revisarla]',
    advanceOnTargetClick: true,
  },
  {
    id: 'action-approve',
    target: '[data-tour="action-approve"]',
    placement: 'top',
    title: 'Tu apruebas, Nexus ejecuta',
    body: 'Los agentes nunca escriben directo a SAP ni a los sistemas. Al aprobar, Nexus crea la Redistribucion en el gemelo y queda auditado quien aprobo, cuando y por que — trazable de la cifra al dato.\n\nEse es el modelo: los agentes proponen y preparan; las personas deciden.',
    action: '[Haz clic en Approve]',
    advanceOnTargetClick: true,
  },
  {
    id: 'pm-tab',
    page: 'activity',
    target: '[data-tour="activity-tab-process"]',
    placement: 'bottom',
    title: 'Los procesos, como son de verdad',
    body: 'Nexus reconstruye el flujo real del negocio a partir de eventos que ya existen en SAP — nadie los estaba leyendo. Aqui viven dos procesos: Pedido a Entrega y Compra a Bodega.',
    action: '[Haz clic en Process Mining]',
    advanceOnTargetClick: true,
  },
  {
    id: 'pm-overview',
    clickSelector: '[data-tour="activity-tab-process"]',
    target: '[data-tour="process-mining"]',
    title: 'Pedido a Entrega (Clientes)',
    body: '340 pedidos reconstruidos: pedido, apartado (lock-in), traslado interno, picking, despacho, entrega, factura y cobro.\n\nEl fill rate ya es 99.7% — el servicio no es el problema. Lo que cuesta dinero es lo que pasa ANTES de la entrega: cuanto tiempo vive el stock apartado.',
  },
  {
    id: 'pm-map',
    target: '[data-tour="pm-tab-map"]',
    placement: 'bottom',
    title: 'El mapa del proceso',
    body: 'En Process Map, el slider de variantes revela lo que el proceso ideal esconde: el loop de apartados vencidos (APARTADO_VENCIDO a APARTADO_RENOVADO, y algunos a PEDIDO_CANCELADO con el stock devuelto ya envejecido) y la rama SIN_STOCK_EN_BODEGA a TRASLADO_INTERNO — el 4.9% del inventario que vive viajando entre bodegas.',
    action: '[Explora la pestana Process Map]',
  },
  {
    id: 'pm-bottlenecks',
    target: '[data-tour="pm-tab-bottlenecks"]',
    placement: 'bottom',
    title: 'Donde se inmoviliza el capital',
    body: 'APARTADO_STOCK es el cuello de botella: el lock-in que envejece mientras el cliente decide. Y de FACTURADO a COBRADO, semanas de cobro segun el tipo de cliente.\n\nHay dos procesos mas: Compra a Bodega, con 5 anos de OCs — ~4 meses de OC a bodega, 73% prepagado, sobrestock que termina en bodega rentada — y Ensamble en Tienda: cada venta de mostrador se arma en sitio, ~45 minutos por mueble, medido por tecnico y sucursal desde el mismo event log.',
    action: '[Explora la pestana Bottlenecks]',
  },
  {
    id: 'compras-tile',
    page: 'apps',
    clickSelector: '[data-tour="app-back"]',
    target: '[data-tour-app*="Planificador de Compras"]',
    title: 'La Jefa de Compras',
    body: 'De compras reactivas a compras predictivas: la orden de compra sugerida por linea, calculada desde 5 anos de historial post-pandemia con afinidad de producto, y consolidada por proveedor en contenedores 40HQ.',
    action: '[Haz clic en la tarjeta]',
    advanceOnTargetClick: true,
  },
  {
    id: 'compras-open',
    clickSelector: '[data-tour-app*="Planificador de Compras"]',
    target: '[data-tour="app-topbar"]',
    placement: 'bottom',
    title: 'Planificador de Compras 40HQ',
    body: 'Cambia el escenario de demanda de base a demanda alta y el plan se recalcula por linea: unidades a reordenar, cuantos 40HQ y la fecha de colocacion segun el lead time real de cada proveedor. Las licitaciones ONCAE quedan fuera del pronostico.\n\nMas abajo: la consolidacion por proveedor y periodo (varias lineas en un solo embarque, nunca un 40HQ medio vacio), el lead time prometido vs real con on-time delivery y precision por proveedor y ruta, y la afinidad de producto: cuando el proveedor cambia el codigo de un SKU, la demanda del anterior sigue al sucesor. El sistema sugiere; la Jefa de Compras decide, con trazabilidad completa.',
  },
  {
    id: 'insights',
    page: 'insights',
    target: '[data-tour="nav-insights"]',
    placement: 'right',
    title: 'Lo que Nexus descubre solo',
    body: 'El motor de correlaciones encontro, sin que nadie se lo pidiera, que bodega aparta mas tiempo, que tipo de cliente tarda mas en cobrar y como el lead time real de cada proveedor se aleja del prometido.',
  },
  {
    id: 'predictions',
    page: 'predictions',
    target: '[data-tour="nav-predictions"]',
    placement: 'right',
    title: 'Predicciones sobre casos abiertos',
    body: 'Decenas de modelos entrenados sobre el historico. Cada pedido abierto se puntua en vivo — por ejemplo, los dias estimados de ciclo o de apartado — con la explicacion de que factores pesan.',
  },
  {
    id: 'whatsapp',
    title: 'El copiloto de bodega',
    body: 'El mismo gemelo atiende WhatsApp. Un bodeguero escribe: "Cuantas sillas ejecutivas L328 hay disponibles en Tegucigalpa? Hay apartadas?"\n\nEl asistente responde con disponible y apartado por bodega, desde cuando esta apartado y para quien, si hay un traslado en curso y en que posicion esta. Menos horas buscando producto; mas clientes atendidos con el mismo equipo.',
  },
  {
    id: 'close',
    title: 'Fin del recorrido',
    body: 'Un solo gemelo digital sobre SAP B1: dashboards por rol, process mining, agentes con aprobacion humana, predicciones y un copiloto por WhatsApp — todo sobre datos que la empresa ya tiene.\n\nEste entorno esta vivo: haz clic donde quieras y explora.',
  },
];

// Per-tenant tour registry: the NavRail launcher and the ?tour=1 auto-start
// show up exactly when the signed-in tenant has an entry here.
export const TOURS: Record<string, TourStep[]> = {
  'tenant-ferco': TOUR_STEPS,
  'tenant-ipsa': IPSA_STEPS,
};
